import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { JourneySummary } from '@/types';

import useGetInfiniteJourneyList from '@/hooks/use-get-infinite-journey-list';
import useGetJourneyListData from '@/hooks/use-get-journey-list-data';
import useGetRoutesDeviationMap from '@/hooks/use-get-routes-deviation-map';
import { FILTER_STATUS } from '@/utils/constants';
import {
  SEARCH_PARAM_ROUTE_TYPE,
  SEARCH_PARAM_SELECTED_DATE,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_SORT_BY,
} from '@/utils/constants/queries';
import formatDate from '@/utils/helpers/format-date';
import { getToday } from '@/utils/helpers/get-formatted-date-for-api';
import isSameDayAsToday from '@/utils/helpers/is-same-day-as-today';

const DEFAULT_FILTER = 'all';
const DEFAULT_SORT = 'deviation';
const DEVIATION_SORT = 'deviation';
const LINE_NUMBER_SORT = 'line_number';
const PAGE_LIMIT = 50;

const useGetRouteList = (searchKey: string) => {
  const [searchParams] = useSearchParams();

  const defaultDateStr = formatDate(getToday());
  const selectedDateStr = searchParams.get(SEARCH_PARAM_SELECTED_DATE) ?? defaultDateStr;
  const sortValue = searchParams.get(SEARCH_PARAM_SORT_BY) || DEFAULT_SORT;
  const selectedFilter = searchParams.get(SEARCH_PARAM_ROUTE_TYPE) ?? DEFAULT_FILTER;
  const isDangerTypeSelected = selectedFilter === FILTER_STATUS.DANGEROUS_GOODS;
  const sortBy = sortValue ?? DEFAULT_SORT;
  const isShowingRoute = searchParams.get(SEARCH_PARAM_SHOW) === 'routes';

  const isToday = isSameDayAsToday(selectedDateStr);

  const isSplitMode = isToday && selectedFilter === 'all' && sortBy === DEVIATION_SORT;

  const apiSort = sortBy === DEVIATION_SORT ? LINE_NUMBER_SORT : sortBy;

  const { deviationMap, activeJourneys } = useGetRoutesDeviationMap();

  const {
    data: activeData,
    isLoading: isActiveLoading,
    isError: isActiveError,
    refetch: refetchActive,
  } = useGetJourneyListData(
    {
      date: selectedDateStr,
      status: 'active',
      sort: apiSort,
      search: searchKey,
      limit: PAGE_LIMIT,
    },
    {
      enabled: isSplitMode && isShowingRoute,
      placeholderData: (prev) => prev,
    }
  );

  const {
    data: infiniteData,
    isLoading: isInfiniteLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isError: isInfiniteError,
    refetch: refetchInfinite,
  } = useGetInfiniteJourneyList({
    date: selectedDateStr,
    status: selectedFilter,
    sort: apiSort,
    search: searchKey,
    limit: PAGE_LIMIT,
    enabled: isShowingRoute,
  });

  const journeys = useMemo(() => {
    const enrichJourney = (journey: JourneySummary) => ({
      ...journey,
      deviation: Number(deviationMap?.[journey.id] ?? journey.deviation) || 0,
      hasDangerousGoods:
        activeJourneys?.find(({ id }) => id === journey.id)?.[
          isDangerTypeSelected ? 'hasDangerousGoods' : 'hasActiveDangerousGoods'
        ] ?? journey.hasDangerousGoods,
    });

    const sortByDeviation = (list: JourneySummary[]) => {
      return [...list].sort((a, b) => (b.deviation ?? 0) - (a.deviation ?? 0));
    };

    if (isSplitMode) {
      const rawActive = activeData ?? [];
      const enrichedActive = rawActive.map(enrichJourney);
      const sortedActive = sortByDeviation(enrichedActive);

      const activeJourneyIds = new Set(rawActive.map((j) => j.id));

      const rawAllOthers = infiniteData?.pages?.flat() ?? [];

      const nonActiveJourneys = rawAllOthers.filter((journey) => !activeJourneyIds.has(journey.id));

      const enrichedOthers = nonActiveJourneys.map(enrichJourney);

      return [...sortedActive, ...enrichedOthers];
    } else {
      const rawAll = infiniteData?.pages?.flat() ?? [];
      const enrichedAll = rawAll.map(enrichJourney);

      if (sortValue === DEVIATION_SORT) {
        return sortByDeviation(enrichedAll);
      }

      return enrichedAll;
    }
  }, [
    isSplitMode,
    activeData,
    infiniteData,
    deviationMap,
    sortValue,
    selectedFilter,
    activeJourneys,
    isDangerTypeSelected,
  ]);

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const refetch = useCallback(() => {
    if (isSplitMode) {
      void refetchActive();
    }

    void refetchInfinite();
  }, [isSplitMode, refetchActive, refetchInfinite]);

  const isRouteLoading = isSplitMode ? isActiveLoading || isInfiniteLoading : isInfiniteLoading;
  const isError = isSplitMode ? isActiveError || isInfiniteError : isInfiniteError;

  return {
    journeys,
    isRouteLoading,
    isLoadingMore: isFetchingNextPage,
    hasMore: hasNextPage,
    loadNextPage,
    isError,
    refetch,
  };
};

export default useGetRouteList;
